import React, { useState, useEffect } from 'react';
import { X, UserCog, Mail, Building2, Phone, Award, Save, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { SearchableDropdown } from './ui/searchable-dropdown';
import { SimpleDropdown } from './ui/simple-dropdown';
import { IMaskInput } from 'react-imask';
import { educationAPI } from '../api/axiosClient';
import { toast } from 'react-hot-toast';

interface SchoolManagerCreateFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface UserFormData {
  firstName: string;
  lastName: string;
  email: string;
  schoolId: string;
  phoneCode: string;
  phoneNumber: string;
  title: string;
  password: string;
  requirePasswordResetOnFirstLogin: boolean;
}

const countryCodes = [
  { code: '90', flag: '🇹🇷', name: 'Türkiye' },
  { code: '1', flag: '🇺🇸', name: 'Amerika' },
  { code: '44', flag: '🇬🇧', name: 'İngiltere' },
  { code: '49', flag: '🇩🇪', name: 'Almanya' },
  { code: '33', flag: '🇫🇷', name: 'Fransa' },
  { code: '39', flag: '🇮🇹', name: 'İtalya' },
  { code: '34', flag: '🇪🇸', name: 'İspanya' },
  { code: '31', flag: '🇳🇱', name: 'Hollanda' },
  { code: '32', flag: '🇧🇪', name: 'Belçika' },
  { code: '41', flag: '🇨🇭', name: 'İsviçre' },
  { code: '43', flag: '🇦🇹', name: 'Avusturya' },
  { code: '45', flag: '🇩🇰', name: 'Danimarka' },
  { code: '46', flag: '🇸🇪', name: 'İsveç' },
  { code: '47', flag: '🇳🇴', name: 'Norveç' },
  { code: '358', flag: '🇫🇮', name: 'Finlandiya' },
  { code: '48', flag: '🇵🇱', name: 'Polonya' },
  { code: '420', flag: '🇨🇿', name: 'Çekya' },
  { code: '421', flag: '🇸🇰', name: 'Slovakya' },
  { code: '36', flag: '🇭🇺', name: 'Macaristan' },
  { code: '40', flag: '🇷🇴', name: 'Romanya' },
  { code: '359', flag: '🇧🇬', name: 'Bulgaristan' },
  { code: '385', flag: '🇭🇷', name: 'Hırvatistan' },
  { code: '386', flag: '🇸🇮', name: 'Slovenya' },
  { code: '372', flag: '🇪🇪', name: 'Estonya' },
  { code: '371', flag: '🇱🇻', name: 'Letonya' },
  { code: '370', flag: '🇱🇹', name: 'Litvanya' },
  { code: '7', flag: '🇷🇺', name: 'Rusya' },
  { code: '380', flag: '🇺🇦', name: 'Ukrayna' },
  { code: '375', flag: '🇧🇾', name: 'Belarus' },
  { code: '370', flag: '🇱🇹', name: 'Litvanya' },
  { code: '371', flag: '🇱🇻', name: 'Letonya' },
  { code: '372', flag: '🇪🇪', name: 'Estonya' },
  { code: '386', flag: '🇸🇮', name: 'Slovenya' },
  { code: '385', flag: '🇭🇷', name: 'Hırvatistan' },
  { code: '359', flag: '🇧🇬', name: 'Bulgaristan' },
  { code: '40', flag: '🇷🇴', name: 'Romanya' },
  { code: '36', flag: '🇭🇺', name: 'Macaristan' },
  { code: '421', flag: '🇸🇰', name: 'Slovakya' },
  { code: '420', flag: '🇨🇿', name: 'Çekya' },
  { code: '48', flag: '🇵🇱', name: 'Polonya' },
  { code: '47', flag: '🇳🇴', name: 'Norveç' },
  { code: '46', flag: '🇸🇪', name: 'İsveç' },
  { code: '45', flag: '🇩🇰', name: 'Danimarka' },
  { code: '43', flag: '🇦🇹', name: 'Avusturya' },
  { code: '41', flag: '🇨🇭', name: 'İsviçre' },
  { code: '32', flag: '🇧🇪', name: 'Belçika' },
  { code: '31', flag: '🇳🇱', name: 'Hollanda' },
  { code: '34', flag: '🇪🇸', name: 'İspanya' },
  { code: '39', flag: '🇮🇹', name: 'İtalya' },
  { code: '33', flag: '🇫🇷', name: 'Fransa' },
  { code: '49', flag: '🇩🇪', name: 'Almanya' },
  { code: '44', flag: '🇬🇧', name: 'İngiltere' },
  { code: '1', flag: '🇺🇸', name: 'Amerika' },
  { code: '90', flag: '🇹🇷', name: 'Türkiye' }
];

const SchoolManagerCreateForm: React.FC<SchoolManagerCreateFormProps> = ({ isOpen, onClose, onSuccess }) => {
  const [userForm, setUserForm] = useState<UserFormData>({
    firstName: '',
    lastName: '',
    email: '',
    schoolId: '',
    phoneCode: '90',
    phoneNumber: '',
    title: '',
    password: '',
    requirePasswordResetOnFirstLogin: true
  });

  const [schools, setSchools] = useState<any[]>([]);
  const [loadingSchools, setLoadingSchools] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Password validation
  const validateUserPassword = (password: string) => {
    const isNumeric = /^\d+$/.test(password);
    const isLength6 = password.length === 6;
    
    return {
      isValid: isNumeric && isLength6,
      isNumeric,
      isLength6
    };
  };

  const userPasswordValidation = validateUserPassword(userForm.password);

  useEffect(() => {
    if (isOpen) {
      // Reset form when opening
      setUserForm({
        firstName: '',
        lastName: '',
        email: '',
        schoolId: '',
        phoneCode: '90',
        phoneNumber: '',
        title: '',
        password: '',
        requirePasswordResetOnFirstLogin: true
      });
      setErrors({}); // Clear errors when form opens

      // Load schools
      fetchSchools();
    }
  }, [isOpen]);

  const handleInputChange = (field: string, value: string) => {
    setUserForm(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!userForm.firstName.trim()) {
      newErrors.firstName = 'Ad gereklidir';
    }

    if (!userForm.lastName.trim()) {
      newErrors.lastName = 'Soyad gereklidir';
    }

    if (!userForm.email.trim()) {
      newErrors.email = 'E-posta gereklidir';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userForm.email)) {
      newErrors.email = 'Geçerli bir e-posta adresi giriniz';
    }

    if (!userForm.schoolId) {
      newErrors.schoolId = 'Okul seçimi gereklidir';
    }

    if (!userForm.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Telefon numarası gereklidir';
    }

    if (!userForm.title.trim()) {
      newErrors.title = 'Unvan gereklidir';
    }

    if (!userForm.password.trim()) {
      newErrors.password = 'Şifre gereklidir';
    } else if (!userPasswordValidation.isValid) {
      newErrors.password = 'Şifre 6 haneli sayı olmalıdır';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const fetchSchools = async () => {
    setLoadingSchools(true);
    try {
      const response = await educationAPI.listSchools({
        Q: '',
        Page: 1,
        Size: 100,
        OrderBy: 1,
        SortBy: 'Name'
      });
      
      if (response.success && response.data) {
        setSchools(response.data.items || []);
      }
    } catch (error) {
      console.error('Error fetching schools:', error);
      toast.error('Okullar yüklenirken bir hata oluştu');
    } finally {
      setLoadingSchools(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const cleanPhoneNumber = userForm.phoneNumber.replace(/\D/g, '');
      
      const schoolManagerRequest = {
        phoneCode: userForm.phoneCode,
        phoneNumber: cleanPhoneNumber,
        email: userForm.email,
        firstName: userForm.firstName,
        lastName: userForm.lastName,
        password: userForm.password,
        schoolId: userForm.schoolId,
        title: userForm.title,
        isPrimary: true,
        requirePasswordResetOnFirstLogin: userForm.requirePasswordResetOnFirstLogin
      };

      const response = await educationAPI.createSchoolManager(schoolManagerRequest);

      if (response.success) {
        toast.success('Okul Yöneticisi başarıyla eklendi');
        onSuccess();
        onClose();
      } else {
        toast.error(response.message || 'Okul Yöneticisi eklenirken bir hata oluştu');
      }
    } catch (error: any) {
      console.error('Error creating school manager:', error);
      const errorMessage = error.response?.data?.message || 'Okul Yöneticisi eklenirken bir hata oluştu';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Yeni Okul Yöneticisi Ekle</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Ad */}
              <div>
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                  <UserCog className="w-4 h-4 text-gray-500" />
                  <span>Ad <span className="text-red-500">*</span></span>
                </label>
                <input
                  type="text"
                  value={userForm.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 transition hover:border-gray-400 ${
                    errors.firstName ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-gray-200'
                  }`}
                  placeholder="Adını girin"
                />
                {errors.firstName && (
                  <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>
                )}
              </div>

              {/* Soyad */}
              <div>
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                  <UserCog className="w-4 h-4 text-gray-500" />
                  <span>Soyad <span className="text-red-500">*</span></span>
                </label>
                <input
                  type="text"
                  value={userForm.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 transition hover:border-gray-400 ${
                    errors.lastName ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-gray-200'
                  }`}
                  placeholder="Soyadını girin"
                />
                {errors.lastName && (
                  <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>
                )}
              </div>

              {/* E-posta */}
              <div>
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                  <Mail className="w-4 h-4 text-gray-500" />
                  <span>E-posta <span className="text-red-500">*</span></span>
                </label>
                <input
                  type="text"
                  value={userForm.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 transition hover:border-gray-400 ${
                    errors.email ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-gray-200'
                  }`}
                  placeholder="ornek@email.com"
                />
                {errors.email && (
                  <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                )}
              </div>

              {/* Okul Seçimi */}
              <div>
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                  <Building2 className="w-4 h-4 text-gray-500" />
                  <span>Okul <span className="text-red-500">*</span></span>
                </label>
                <SearchableDropdown
                  options={schools.map(school => ({
                    id: school.id,
                    label: school.name,
                    value: school.id
                  }))}
                  value={userForm.schoolId ? {
                    id: userForm.schoolId,
                    label: schools.find(s => s.id === userForm.schoolId)?.name || '',
                    value: userForm.schoolId
                  } : null}
                  onChange={(school) => {
                    if (school) {
                      handleInputChange('schoolId', school.value as string);
                    } else {
                      handleInputChange('schoolId', '');
                    }
                  }}
                  placeholder="Okul ara veya seç..."
                  clearable={true}
                  isLoading={loadingSchools}
                  noResultsText="Okul bulunamadı"
                />
                {errors.schoolId && (
                  <p className="text-red-500 text-xs mt-1">{errors.schoolId}</p>
                )}
              </div>

              {/* Telefon */}
              <div>
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                  <Phone className="w-4 h-4 text-gray-500" />
                  <span>Telefon <span className="text-red-500">*</span></span>
                </label>
                <div className="flex space-x-2">
                  <SimpleDropdown
                    options={countryCodes.map((code) => ({
                      id: code.code,
                      label: `${code.flag} +${code.code}`,
                      value: code.code
                    }))}
                    value={countryCodes.find(country => country.code === userForm.phoneCode) ? {
                      id: userForm.phoneCode,
                      label: `${countryCodes.find(country => country.code === userForm.phoneCode)?.flag} +${userForm.phoneCode}`,
                      value: userForm.phoneCode
                    } : null}
                    onChange={(countryCode) => {
                      if (countryCode) {
                        setUserForm(prev => ({ 
                          ...prev, 
                          phoneCode: String(countryCode.value),
                          phoneNumber: '' // Reset phone number when country changes
                        }));
                      }
                    }}
                    placeholder="Ülke seçiniz"
                    className="w-24"
                  />
                  <IMaskInput
                    mask="(000) 000-0000"
                    value={userForm.phoneNumber}
                    onAccept={(value) => handleInputChange('phoneNumber', value || '')}
                    className={`flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 transition hover:border-gray-400 text-sm ${
                      errors.phoneNumber ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-gray-200'
                    }`}
                    placeholder="(555) 123-4567"
                  />
                </div>
                {errors.phoneNumber && (
                  <p className="text-red-500 text-xs mt-1">{errors.phoneNumber}</p>
                )}
              </div>

              {/* Unvan */}
              <div>
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                  <Award className="w-4 h-4 text-gray-500" />
                  <span>Unvan <span className="text-red-500">*</span></span>
                </label>
                <input
                  type="text"
                  value={userForm.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 transition hover:border-gray-400 ${
                    errors.title ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-gray-200'
                  }`}
                  placeholder="Örn: Müdür, Müdür Yardımcısı"
                />
                {errors.title && (
                  <p className="text-red-500 text-xs mt-1">{errors.title}</p>
                )}
              </div>
            </div>

            {/* Şifre ve Checkbox */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Şifre */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Şifre <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    placeholder="••••••"
                    value={userForm.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    className={`w-full pr-10 text-left text-sm font-mono px-3 py-2 border rounded-md focus:outline-none focus:ring-2 transition hover:border-gray-400 ${
                      errors.password ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-gray-200'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                
                {/* Password Strength Indicator */}
                {userForm.password && (
                  <div className="space-y-2">
                    <div className="flex space-x-2">
                      <div className={`w-1/2 h-2 rounded-full ${
                        userPasswordValidation.isNumeric ? 'bg-green-500' : 'bg-gray-200'
                      }`}></div>
                      <div className={`w-1/2 h-2 rounded-full ${
                        userPasswordValidation.isLength6 ? 'bg-green-500' : 'bg-gray-200'
                      }`}></div>
                    </div>
                    <div className="text-xs text-gray-500 space-y-1">
                      <div className={`flex items-center space-x-2 ${userPasswordValidation.isNumeric ? 'text-green-600' : 'text-gray-400'}`}>
                        <div className={`w-2 h-2 rounded-full ${userPasswordValidation.isNumeric ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                        <span>Sadece rakam</span>
                      </div>
                      <div className={`flex items-center space-x-2 ${userPasswordValidation.isLength6 ? 'text-green-600' : 'text-gray-400'}`}>
                        <div className={`w-2 h-2 rounded-full ${userPasswordValidation.isLength6 ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                        <span>6 haneli</span>
                      </div>
                    </div>
                  </div>
                )}
                {errors.password && (
                  <p className="text-red-500 text-xs mt-1">{errors.password}</p>
                )}
              </div>

              {/* İlk girişte şifre sıfırlama gerektir */}
              <div className="space-y-2 min-h-[72px] flex flex-col justify-center">
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="requirePasswordReset"
                    checked={userForm.requirePasswordResetOnFirstLogin}
                    onChange={(e) => setUserForm(prev => ({ ...prev, requirePasswordResetOnFirstLogin: e.target.checked }))}
                    className="w-4 h-4 text-[#9B51E0] bg-gray-100 border-gray-300 rounded focus:ring-[#9B51E0] focus:ring-2"
                  />
                  <label htmlFor="requirePasswordReset" className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                    <CheckCircle className="w-4 h-4 text-gray-500" />
                    <span>İlk girişte şifre sıfırlama gerektir</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex justify-end space-x-3 pt-8 border-t border-gray-200">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isSubmitting}
              >
                İptal
              </Button>
              <Button
                type="submit"
                className="bg-[#9B51E0] hover:bg-[#8B41D0]"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Ekleniyor...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Kaydet
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SchoolManagerCreateForm;
